import { type ReactNode } from 'react'
import { SideSheetHostView, SideSheetMain } from '@/components/SideSheet'
import { Sandwich, SandwichBun, SandwichContent } from '@/components/Sandwich'
import {
  StickyViewHeader,
  StickyViewHeaderExtras,
  StickyViewHeaderMain,
  StickyViewHeaderSpacer,
} from '@/components/StickyViewHeader'
import { ViewHeading } from '@/components/ViewHeading'
import { ContentLayout, ContentMain } from '@/components/ContentLayout'
import { type MotionValue } from 'motion/react'
import { useScrollOffsets } from '@/hooks/element-hooks'
import { ListHeader } from '@/components/ListHeader'
import {
  RadioButtonGroup,
  RadioButtonItem,
} from '@/components/RadioButtonGroup'
import { type AccountMergeRequestFilterOption } from '@/features/admin/account-linking/AccountMergeRequestStore'

export interface MergeAccountsScreenProps {
  filterOption: AccountMergeRequestFilterOption
  onFilterOptionChange: (filterOption: AccountMergeRequestFilterOption) => void
  total: number
  children?: ReactNode
}

export function MergeAccountsScreen({
  filterOption,
  onFilterOptionChange,
  total,
  children,
}: MergeAccountsScreenProps) {
  const { connectRef: scrollRef, scrollYStart: scrollOffset } =
    useScrollOffsets()

  return (
    <SideSheetHostView>
      <SideSheetMain>
        <Sandwich stretch>
          <SandwichBun>
            <MergeAccountsHeader scrollOffset={scrollOffset}>
              <RadioButtonGroup size="medium">
                <RadioButtonItem
                  active={filterOption === 'Pending'}
                  onPress={() => onFilterOptionChange('Pending')}
                >
                  Pending
                </RadioButtonItem>
                <RadioButtonItem
                  active={filterOption === 'Processed'}
                  onPress={() => onFilterOptionChange('Processed')}
                >
                  Processed
                </RadioButtonItem>
              </RadioButtonGroup>
            </MergeAccountsHeader>
          </SandwichBun>
          <SandwichContent stretch>
            <ContentLayout
              accountForLabel
              accountForTabBar
              fill
              ref={scrollRef}
            >
              <ContentMain fill constrainWidth>
                <Sandwich stretch>
                  <SandwichBun>
                    <ListHeader count={total}>Requests</ListHeader>
                  </SandwichBun>
                  <SandwichContent>{children}</SandwichContent>
                </Sandwich>
              </ContentMain>
            </ContentLayout>
          </SandwichContent>
        </Sandwich>
      </SideSheetMain>
    </SideSheetHostView>
  )
}

interface MergeAccountsHeaderProps {
  scrollOffset: MotionValue<number>
  children?: ReactNode
}

function MergeAccountsHeader({
  children,
  scrollOffset,
}: MergeAccountsHeaderProps) {
  return (
    <StickyViewHeader bordered scrollOffset={scrollOffset}>
      <StickyViewHeaderMain>
        <ViewHeading
          primaryText="Merge Accounts"
          secondaryText="All workspaces"
        />
      </StickyViewHeaderMain>
      <StickyViewHeaderSpacer />
      <StickyViewHeaderExtras>{children}</StickyViewHeaderExtras>
    </StickyViewHeader>
  )
}
